<template>
  <v-dialog v-model="localModelValue" max-width="716px" persistent>
    <v-card class="upload-modal-card">
      <div class="modal-content">
        <h3 class="modal-title">Upload Module</h3>

        <div class="modal-divider"></div>

        <div class="tabs-container">
          <button
            class="tab-button"
            :class="{ active: activeTab === 'manual' }"
            @click="activeTab = 'manual'"
          >
            Upload Manually
          </button>
          <button
            class="tab-button"
            :class="{ active: activeTab === 'scorm' }"
            @click="activeTab = 'scorm'"
          >
            Choose From Scorm Files
          </button>
        </div>

        <!-- Upload Manually Tab Content -->
        <div v-if="activeTab === 'manual'">
          <input
            ref="fileInputRef"
            type="file"
            @change="handleFileSelect"
            style="display: none"
            accept=".zip"
          />
          <div
            v-if="!selectedFile"
            class="upload-area"
            :class="{ 'drag-over': isDragOver }"
            @drop="handleDrop"
            @dragover.prevent="handleDragOver"
            @dragenter.prevent="isDragOver = true"
            @dragleave.prevent="isDragOver = false"
            @click="triggerFileInput"
          >
            <div class="upload-button-wrapper">
              <span class="upload-button-text">Choose File</span>
              <svg
                width="43"
                height="43"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 5V19M5 12H19"
                  stroke="#003d6a"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <p class="upload-hint">Or drag and drop them here</p>
            <input
              ref="fileInputRef"
              type="file"
              @change="handleFileSelect"
              style="display: none"
              accept=".zip"
            />
          </div>

          <div v-if="selectedFile" class="file-preview-card">
            <div class="file-preview-header">
              <h4 class="file-preview-title">{{ selectedFile.name }}</h4>
              <div class="file-preview-divider"></div>
            </div>
            <div class="file-preview-actions">
              <button class="remove-file-btn" @click="removeFile">
                Remove File
              </button>
              <button class="change-file-btn" @click="triggerFileInput">
                Change File
              </button>
            </div>
          </div>
        </div>

        <!-- Choose From Scorm Files Tab Content -->
        <div v-else class="scorm-select-container">
          <label class="scorm-label">Module</label>
          <SingleSelect
            v-model="selectedScormId"
            :options="scormOptions"
            placeholder="Choose module from scorm files"
            variant="bordered"
            :teleport="true"
            @change="handleScormSelect"
          />
        </div>

        <div class="modal-footer">
          <BaseButton
            variant="outline"
            class="cancel-button"
            @click="handleCancel"
          >
            Cancel
          </BaseButton>
          <BaseButton
            class="save-button"
            :disabled="
              (activeTab === 'manual' && !selectedFile) ||
              (activeTab === 'scorm' && !selectedScorm) ||
              saving ||
              uploading
            "
            @click="handleSave"
            :loading="saving || uploading"
            :loading-text="saving || uploading ? 'Saving...' : 'Save Module'"
          >
            Save Module
          </BaseButton>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, onMounted, watch, defineProps, defineEmits } from "vue";
import BaseButton from "@/components/common/BaseButton.vue";
import SingleSelect from "@/components/common/SingleSelect.vue";
import scormFilesService from "@/services/scormFilesService";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "save", "cancel"]);

const localModelValue = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const activeTab = ref("manual");
const selectedFile = ref(null);
const selectedScormId = ref("");
const selectedScorm = ref(null);
const isDragOver = ref(false);
const fileInputRef = ref(null);
const saving = ref(false);
const uploading = ref(false);
const scormOptions = ref([]);
const loadingScorm = ref(false);

const triggerFileInput = () => {
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
    fileInputRef.value.click();
  }
};

const handleFileSelect = (event) => {
  const file = event.target.files[0];
  if (file) {
    if (!file.name.toLowerCase().endsWith(".zip")) {
      alert("Please select a .zip file");
      if (fileInputRef.value) {
        fileInputRef.value.value = "";
      }
      return;
    }
    selectedFile.value = file;
    isDragOver.value = false;
  }
};

const handleDragOver = (event) => {
  event.preventDefault();
  isDragOver.value = true;
};

const handleDrop = (event) => {
  event.preventDefault();
  isDragOver.value = false;

  const file = event.dataTransfer.files[0];
  if (file) {
    if (!file.name.toLowerCase().endsWith(".zip")) {
      alert("Please select a .zip file");
      return;
    }
    selectedFile.value = file;
  }
};

const resetFields = () => {
  selectedFile.value = null;
  selectedScormId.value = "";
  selectedScorm.value = null;
  activeTab.value = "manual";
  isDragOver.value = false;
  uploading.value = false;
  saving.value = false;
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
};

const removeFile = () => {
  selectedFile.value = null;
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
};

const handleCancel = () => {
  resetFields();
  emit("cancel");
  localModelValue.value = false;
};

watch(localModelValue, (newValue) => {
  if (!newValue) {
    resetFields();
  }
});

const fetchScormFiles = async () => {
  if (loadingScorm.value || scormOptions.value.length > 0) return;

  try {
    loadingScorm.value = true;
    const response = await scormFilesService.getAll();

    if (response && response.data && Array.isArray(response.data)) {
      scormOptions.value = response.data.map((scorm) => ({
        text: scorm.title || `Scorm files ${scorm.id}`,
        value: scorm.uuid || scorm.id,
        ...scorm,
      }));
    }
  } catch (error) {
    console.error("Error fetching SCORM files:", error);
  } finally {
    loadingScorm.value = false;
  }
};

const handleScormSelect = (data) => {
  if (data && data.option) {
    selectedScorm.value = data.option;
  }
};

const handleSave = async () => {
  if (saving.value || uploading.value) return;

  if (activeTab.value === "manual" && !selectedFile.value) return;
  if (activeTab.value === "scorm" && !selectedScorm.value) return;

  uploading.value = true;
  saving.value = true;
  emit("save", {
    file: activeTab.value === "manual" ? selectedFile.value : null,
    scorm: activeTab.value === "scorm" ? selectedScorm.value : null,
    tab: activeTab.value,
  });
};

watch(activeTab, (newTab) => {
  if (newTab === "scorm") {
    fetchScormFiles();
  } else {
    selectedScormId.value = "";
    selectedScorm.value = null;
  }
});

onMounted(() => {
  if (activeTab.value === "scorm") {
    fetchScormFiles();
  }
});
</script>

<style scoped>
.upload-modal-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow:
    0px 1px 2px 0px rgba(0, 0, 0, 0.3),
    0px 2px 6px 2px rgba(0, 0, 0, 0.15);
  background-color: white;
}

.modal-content {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.modal-title {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-large);
  font-weight: 500;
  line-height: 28px;
  color: #3b4648;
  margin: 0;
}

.modal-divider {
  height: 1px;
  width: 100%;
  background-color: #e0e0e0;
}

.tabs-container {
  display: flex;
  gap: 16px;
  align-items: center;
}

.tab-button {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 700;
  line-height: 24px;
  padding: 12px 20px;
  height: 44px;
  border-radius: 24px;
  border: 1px solid #dfe2e7;
  background-color: white;
  color: #40444b;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.tab-button.active {
  background-color: #e00069;
  border-color: #e00069;
  color: white;
}

.tab-button:hover:not(.active) {
  border-color: var(--primary-blue);
}

.upload-area {
  border: 1px dashed #0478ce;
  border-radius: 16px;
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px;
  cursor: pointer;
  transition: all 0.2s;
  background-color: white;
}

.upload-area:hover {
  background-color: #f8f9fa;
}

.upload-area.drag-over {
  background-color: #e8f4f8;
  border-color: var(--primary-blue);
}

.upload-button-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 48px;
  border-radius: 16px;
}

.upload-button-text {
  font-family: "Inter", sans-serif;
  font-size: var(--fs-xl);
  font-weight: 700;
  line-height: 32px;
  color: var(--primary-blue);
  white-space: nowrap;
}

.upload-hint {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 400;
  line-height: 24px;
  color: #98a2b3;
  margin-top: 12px;
  text-align: center;
}

.file-preview-card {
  background: white;
  border: 1px solid #0478ce;
  border-radius: 24px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.file-preview-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.file-preview-title {
  font-family: "League Spartan", sans-serif;
  font-size: 32px;
  font-weight: 700;
  line-height: 40px;
  color: var(--primary-blue);
  margin: 0;
  word-break: break-word;
}

.file-preview-divider {
  height: 1px;
  width: 100%;
  background-color: #e0e0e0;
}

.file-preview-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
}

.remove-file-btn {
  flex: 1;
  height: 48px;
  padding: 10px 16px;
  border-radius: 12px;
  border: none;
  background: transparent;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 700;
  line-height: 24px;
  color: #3b4648;
  cursor: pointer;
  transition: background-color 0.2s;
}

.remove-file-btn:hover {
  background-color: #f8f9fa;
}

.change-file-btn {
  flex: 1;
  height: 48px;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid var(--primary-blue);
  background: white;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 700;
  line-height: 24px;
  color: var(--primary-blue);
  cursor: pointer;
  transition: all 0.2s;
}

.change-file-btn:hover {
  background-color: #f8f9fa;
}

.modal-footer {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
}

.cancel-button {
  width: 126px;
  height: 56px;
  border-radius: 12px;
  border: 1px solid #bac1cc;
  background-color: white;
  color: #6c737f;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 700;
  line-height: 24px;
}

.cancel-button:hover {
  background-color: #f8f9fa;
}

.save-button {
  width: 126px;
  height: 56px;
  border-radius: 12px;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 700;
  line-height: 24px;
  border: none;
}

.save-button:disabled {
  background-color: #dfe2e7 !important;
  color: #cccccc !important;
  cursor: not-allowed;
  opacity: 1;
}

.save-button:not(:disabled) {
  background-color: var(--primary-blue) !important;
  color: white !important;
}

.save-button:not(:disabled):hover {
  background-color: #00599a !important;
}

.scorm-select-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.scorm-label {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 500;
  line-height: 24px;
  color: #3b4648;
}

@media (max-width: 768px) {
  .modal-content {
    padding: 16px;
    gap: 16px;
  }

  .tabs-container {
    flex-direction: column;
    gap: 8px;
  }

  .tab-button {
    width: 100%;
  }

  .upload-area {
    height: 250px;
    padding: 20px;
  }

  .upload-button-text {
    font-size: var(--fs-big);
  }

  .modal-footer {
    flex-direction: column;
    width: 100%;
  }

  .cancel-button,
  .save-button {
    width: 100%;
  }
}
</style>
